import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import api from '../../api';
import { theme } from '../../theme';
import { CoworkState, CoworkTask } from '../../types/cowork';
import {
  ActionBar,
  Button,
  Input,
  Muted,
  Select,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  SurfaceTitle,
  TabBar,
  TabBtn,
} from '../ui';

const Row = styled.div<{ $focused?: boolean; $dragging?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.6rem;
  border-radius: 9px;
  border: 1px solid ${({ $focused }) => ($focused ? 'rgba(255, 20, 147, 0.35)' : 'rgba(255, 20, 147, 0.1)')};
  background: ${({ $focused }) => ($focused ? 'rgba(255, 20, 147, 0.09)' : 'rgba(10, 7, 22, 0.4)')};
  opacity: ${({ $dragging }) => ($dragging ? 0.4 : 1)};
  cursor: default;
`;

const Handle = styled.span`
  cursor: grab;
  color: ${theme.colors.textMuted};
  font-size: 0.9rem;
  user-select: none;
  padding: 0 0.15rem;
`;

const Text = styled.span<{ $done?: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: 0.88rem;
  color: ${({ $done }) => ($done ? theme.colors.textMuted : '#fff')};
  text-decoration: ${({ $done }) => ($done ? 'line-through' : 'none')};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Chip = styled.span<{ $tone?: 'high' | 'focus' }>`
  flex-shrink: 0;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  padding: 0.12rem 0.42rem;
  border-radius: 999px;
  text-transform: uppercase;
  background: ${({ $tone }) => ($tone === 'high' ? 'rgba(255, 77, 109, 0.18)' : 'rgba(255, 20, 147, 0.18)')};
  color: ${({ $tone }) => ($tone === 'high' ? theme.colors.danger : theme.colors.pink)};
`;

type Filter = 'active' | 'completed' | 'archived';

interface Props {
  state: CoworkState;
  run: (request: Promise<{ data: any }>) => Promise<boolean>;
  refresh: () => Promise<void>;
}

const CoworkTasksTab: React.FC<Props> = ({ state, run, refresh }) => {
  const [text, setText] = useState('');
  const [priority, setPriority] = useState<'normal' | 'high'>('normal');
  const [filter, setFilter] = useState<Filter>('active');
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [archived, setArchived] = useState<CoworkTask[]>([]);

  const tasks = useMemo(() => {
    if (filter === 'archived') return archived;
    if (filter === 'completed') return state.streamerTasks.filter((t) => t.status === 'completed');
    return state.streamerTasks.filter((t) => t.status === 'pending');
  }, [filter, state.streamerTasks, archived]);

  const loadArchived = async () => {
    try {
      const { data } = await api.get<CoworkTask[]>('/cowork/tasks', { params: { includeArchived: true } });
      setArchived(data.filter((t) => t.status === 'archived'));
    } catch {
      setArchived([]);
    }
  };

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    const value = text.trim();
    if (!value) return;
    setText('');
    await run(api.post('/cowork/tasks', { text: value, priority }));
    await refresh();
  };

  const saveEdit = async () => {
    if (!editing) return;
    const value = editing.value.trim();
    setEditing(null);
    if (!value) return;
    await run(api.put(`/cowork/tasks/${editing.id}`, { text: value }));
    await refresh();
  };

  const onDrop = async (targetId: string) => {
    if (!dragId || dragId === targetId) return;

    const ordered = state.streamerTasks.filter((t) => t.status !== 'archived').map((t) => t.id);
    const from = ordered.indexOf(dragId);
    const to = ordered.indexOf(targetId);
    if (from < 0 || to < 0) return;

    ordered.splice(to, 0, ordered.splice(from, 1)[0]);
    setDragId(null);
    await run(api.post('/cowork/tasks/reorder', { ids: ordered }));
    await refresh();
  };

  return (
    <Surface>
      <SurfaceHeader>
        <SurfaceTitle>Mis tareas</SurfaceTitle>
        <TabBar>
          <TabBtn $active={filter === 'active'} onClick={() => setFilter('active')}>Pendientes</TabBtn>
          <TabBtn $active={filter === 'completed'} onClick={() => setFilter('completed')}>Completadas</TabBtn>
          <TabBtn
            $active={filter === 'archived'}
            onClick={() => { setFilter('archived'); loadArchived(); }}
          >
            Archivadas
          </TabBtn>
        </TabBar>
      </SurfaceHeader>

      <SurfaceBody>
        <form onSubmit={create} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.85rem' }}>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Nueva tarea — escribí y apretá Enter"
          />
          <Select
            value={priority}
            onChange={(e) => setPriority(e.target.value as 'normal' | 'high')}
            style={{ width: 140 }}
          >
            <option value="normal">Normal</option>
            <option value="high">Prioridad</option>
          </Select>
          <Button type="submit">Agregar</Button>
        </form>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {tasks.length === 0 && <Muted>No hay tareas en esta vista.</Muted>}

          {tasks.map((task) => (
            <Row
              key={task.id}
              $focused={task.focused}
              $dragging={dragId === task.id}
              draggable={filter === 'active'}
              onDragStart={() => setDragId(task.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(task.id)}
            >
              {filter === 'active' && <Handle title="Arrastrá para reordenar">⠿</Handle>}

              {editing?.id === task.id ? (
                <Input
                  autoFocus
                  value={editing.value}
                  onChange={(e) => setEditing({ id: task.id, value: e.target.value })}
                  onBlur={saveEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEdit();
                    if (e.key === 'Escape') setEditing(null);
                  }}
                />
              ) : (
                <Text $done={task.status === 'completed'} onDoubleClick={() => setEditing({ id: task.id, value: task.text })}>
                  {task.text}
                </Text>
              )}

              {task.focused && <Chip $tone="focus">Foco</Chip>}
              {task.priority === 'high' && task.status === 'pending' && <Chip $tone="high">Prioridad</Chip>}

              <ActionBar>
                {task.status === 'pending' && !task.focused && (
                  <Button $variant="ghost" $size="sm" onClick={() => run(api.post(`/cowork/tasks/${task.id}/focus`))}>
                    Foco
                  </Button>
                )}
                {task.status !== 'archived' && (
                  <Button
                    $variant="ghost"
                    $size="sm"
                    onClick={() => run(api.put(`/cowork/tasks/${task.id}`, {
                      status: task.status === 'completed' ? 'pending' : 'completed',
                    }))}
                  >
                    {task.status === 'completed' ? 'Reabrir' : 'Listo'}
                  </Button>
                )}
                <Button $variant="ghost" $size="sm" onClick={() => setEditing({ id: task.id, value: task.text })}>
                  Editar
                </Button>
                {task.status !== 'archived' ? (
                  <Button
                    $variant="ghost"
                    $size="sm"
                    onClick={() => run(api.put(`/cowork/tasks/${task.id}`, { status: 'archived' }))}
                  >
                    Archivar
                  </Button>
                ) : (
                  <Button
                    $variant="ghost"
                    $size="sm"
                    onClick={async () => {
                      await run(api.put(`/cowork/tasks/${task.id}`, { status: 'pending' }));
                      await loadArchived();
                    }}
                  >
                    Restaurar
                  </Button>
                )}
                <Button
                  $variant="danger"
                  $size="sm"
                  onClick={async () => {
                    await run(api.delete(`/cowork/tasks/${task.id}`));
                    if (filter === 'archived') await loadArchived();
                  }}
                >
                  Borrar
                </Button>
              </ActionBar>
            </Row>
          ))}
        </div>

        {filter === 'active' && tasks.length > 1 && (
          <Muted>Arrastrá desde ⠿ para cambiar el orden. Doble clic sobre el texto para editarlo.</Muted>
        )}
      </SurfaceBody>
    </Surface>
  );
};

export default CoworkTasksTab;
